import React, { useState, useRef, useEffect } from "react"
import axios from 'axios'
import SortIcon from 'mdi-react/SortIcon';
import { Row, Col, Button, Container, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import Select from 'react-select'

import SortAscendingIcon from 'mdi-react/SortAscendingIcon';
import SortDescendingIcon from 'mdi-react/SortDescendingIcon';
import DatePicker from 'react-datepicker';
import { dateStrToYmd } from '@/shared/helpers';
import MultipleSelectField from '@/shared/components/form/MultipleSelectField'
import ProgramsHierarchy from '@/shared/components/ProgramsHierarchy'
import MerchantsHierarchy from '@/shared/components/MerchantsHierarchy'
import {
    getReportFilterPresets,
    createReportFilterPreset,
    updateReportFilterPreset,
    deleteReportFilterPreset
} from '@/shared/apiHelper'
import { isEqual, clone } from 'lodash';
import { CSVLink } from "react-csv";

import { setOrganization } from '@/redux/actions/organizationActions';
import store from '@/containers/App/store';
import { setOrganization as setAuthOrganization } from "@/containers/App/auth";
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import ExportButton from '@/shared/components/ExportButton';
const QUERY_TRIGGER = 'QUERY_TRIGGER';
const PAGE_CHANGED = 'PAGE_CHANGED';
const PAGE_SIZE_CHANGED = 'PAGE_SIZE_CHANGED';
const PAGE_SORT_CHANGED = 'PAGE_SORT_CHANGED'
const PAGE_FILTER_CHANGED = 'PAGE_FILTER_CHANGED';
const TOTAL_COUNT_CHANGED = 'TOTAL_COUNT_CHANGED';

export const reducer = (state, { type, payload }) => {
    switch (type) {
        case PAGE_CHANGED:
            return {
                ...state,
                queryPageIndex: payload,
            };
        case PAGE_SIZE_CHANGED:
            return {
                ...state,
                queryPageSize: payload,
            };
        case PAGE_SORT_CHANGED:
            return {
                ...state,
                queryPageSortBy: payload,
            };
        case PAGE_FILTER_CHANGED:
            return {
                ...state,
                queryPageFilter: payload,
            };
        case TOTAL_COUNT_CHANGED:
            return {
                ...state,
                totalCount: payload,
            };
        case QUERY_TRIGGER:
            return {
                ...state,
                queryTrigger: payload,
            };
        default:
            throw new Error(`Unhandled action type: ${type}`);
    }
};

export const useEffectToDispatch = (dispatch, {
    pageIndex = 0,
    pageSize,
    gotoPage,
    sortBy,
    filter,
    data,
    useFilter,
    trigger = 0
}) => {
    React.useEffect(() => {
        dispatch({ type: PAGE_CHANGED, payload: pageIndex });
    }, [pageIndex]);

    React.useEffect(() => {
        if (gotoPage) {
            dispatch({ type: PAGE_SIZE_CHANGED, payload: pageSize });
            gotoPage(0);
        }
    }, [pageSize, gotoPage]);

    React.useEffect(() => {
        if (gotoPage) {
            dispatch({ type: PAGE_SORT_CHANGED, payload: sortBy });
            gotoPage(0);
        }
    }, [sortBy, gotoPage]);

    React.useEffect(() => {
        if (gotoPage) {
            if (useFilter) {
                dispatch({ type: PAGE_FILTER_CHANGED, payload: filter });
                gotoPage(0);
            }
        }
    }, [filter, gotoPage, useFilter]);

    React.useEffect(() => {
        if (data?.count) {
            dispatch({
                type: TOTAL_COUNT_CHANGED,
                payload: data.count,
            });
        }
    }, [data?.count]);

    React.useEffect(() => {
        if (gotoPage && trigger !== null) {
          dispatch({ type: QUERY_TRIGGER, payload: trigger });
          gotoPage(pageIndex); // stay on current page
        }
      }, [trigger, gotoPage, pageIndex]);
}

const getFirstDayOfMonth = () => {
    let date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1)
}

const getFirstDay = () => {
    let date = new Date();
    return new Date(date.getFullYear(), 0, 1)
}

const defaultFrom = getFirstDay()
const defaultTo = new Date()

export const initialState = {
    queryPageIndex: 0,
    queryPageSize: 10,
    totalCount: 0,
    queryPageFilter: {},
    queryPageSortBy: [],
    queryTrigger: 0,
};

/**
 * Why: a report's table request and its CSV export must ask the API for the same dates. A filter
 * hands over either the date picker's Date or a "YYYY-MM-DD" string it already formatted; only a
 * Date needs formatting. Formatting a string again reads it as UTC midnight and moves it back a day
 * in browsers west of UTC.
 *
 * Scope: formats a from/to Date as its local calendar day ("YYYY-MM-DD", or "MM/DD/YYYY" for the
 * points-reserve report); returns every other value unchanged.
 *
 * SRP: the single place a report request's from/to is formatted — shared by the table request
 * (fetchApiData) and the export request (fetchApiDataExport) so the two cannot drift apart again.
 */
export const formatDateFilterParam = (value, url) => {
    if (!(value instanceof Date)) {
        return value;
    }

    const yyyy = value.getFullYear();
    const mm = String(value.getMonth() + 1).padStart(2, '0');
    const dd = String(value.getDate()).padStart(2, '0');

    if (url && url.includes('report/points-reserve')) {
        return `${mm}/${dd}/${yyyy}`;
    }

    return `${yyyy}-${mm}-${dd}`;
};

// export const fetchApiData = async (apiUrl, page, pageSize, pageFilterO = null, pageSortBy) => {
export const fetchApiData = async (queryParams) => {

    const queryDefaults = {
        url: '/',
        page: initialState.queryPageIndex,
        size: initialState.queryPageSize,
        filter: initialState.queryPageFilter,
        sortby: initialState.queryPageSortBy,
        trigger: initialState.queryTrigger
    }

    const options = { ...queryDefaults, ...queryParams }

    const params = []
    let paramStr = ''
    if (options.trigger > 0) {
        params.push(`t=${options.trigger}`)
    }
    if (options.filter) {
        const fields = Object.keys(options.filter);
        if (fields.length > 0) {
            for (var i in fields) {
                let value = options.filter[fields[i]];
                if (fields[i] === 'from' || fields[i] === 'to') {
                    value = formatDateFilterParam(value, options.url);
                }
                if (Array.isArray(value)) {
                    value = value.filter(v => v !== null && v !== undefined && v !== '');
                    if (value.length === 0) continue;
                } else if (value === undefined || value === null) {
                    continue;
                }
                params.push(`${fields[i]}=${encodeURIComponent(value)}`)
            }
        }
        // if(options.filter.keyword !== 'undefined' && options.filter.keyword) params.push(`keyword=${options.filter.keyword}`)
        // if(options.filter.from !== 'undefined' && options.filter.from) params.push(`from=${options.filter.from}`)
        // if(options.filter.to !== 'undefined' && options.filter.to) params.push(`to=${options.filter.to}`)
    }
    if (params.length > 0) {
        paramStr = params.join('&')
    }
    // console.log(paramStr)
    if (options.sortby.length > 0) {
        const sortParams = options.sortby[0];
        const sortyByDir = sortParams.desc ? 'desc' : 'asc'
        paramStr = `${paramStr}&sortby=${sortParams.id}&direction=${sortyByDir}`
    }
    let glue = '?'
    if (options.url.indexOf('?') > 0) {
        glue = '&'
    }
    let apiUrl = `${options.url}${glue}page=${options.page + 1}&limit=${options.size}&${paramStr}`
    try {
        const response = await axios.get(
            apiUrl,
            options.requestConfig
        );
        // console.log(response)
        if (response.data.length === 0) return { results: [], count: 0 }
        const data = {
            results: response.data.data,
            count: response.data.total,
            config: response.data.config ? response.data.config : [],
            full: response.data,
            // The axios interceptor resolves 4xx responses, so callers need the status to tell
            // an error payload from a page of rows.
            status: response.status,
            total_amount: response.data.total_amount,
            total_points: response.data.total_points,
        };
        // console.log(data)
        return data;
    } catch (e) {
        throw new Error(`API error:${e?.message}`);
    }
};

export const fetchApiDataExport = async (queryParams) => {
    const queryDefaults = {
        url: '/',
        filter: initialState.queryPageFilter,
        sortby: initialState.queryPageSortBy,
        trigger: initialState.queryTrigger,
        visibleColumns: null
    }

    const options = { ...queryDefaults, ...queryParams }

    // console.log(options)

    const params = []
    let paramStr = ''
    if (options.trigger > 0) {
        params.push(`t=${options.trigger}`)
    }
    if (options.filter) {
        const fields = Object.keys(options.filter);
        if (fields.length > 0) {
            for (var i in fields) {
                let value = options.filter[fields[i]];
                if (fields[i] === 'from' || fields[i] === 'to') {
                    value = formatDateFilterParam(value, options.url);
                }
                params.push(`${fields[i]}=${encodeURIComponent(value)}`)
            }
        }
    }
    if (params.length > 0) {
        paramStr = params.join('&')
    }
    if (options.sortby.length > 0) {
        const sortParams = options.sortby[0];
        const sortyByDir = sortParams.desc ? 'desc' : 'asc'
        paramStr = `${paramStr}&sortby=${sortParams.id}&direction=${sortyByDir}`
    }
    try {
        const response = await axios.get(
            `${options.url}?${paramStr}`
        );
        if (response.data.length === 0) return { results: [], count: 0 }
        const headers = options.visibleColumns
            ? (response.data?.headers || []).filter((header) => options.visibleColumns.includes(header.key))
            : response.data?.headers;
        const data = {
            results: response.data.data,
            headers,
            count: response.data.total
        };
        return data;
    } catch (e) {
        throw new Error(`API error:${e?.message}`);
    }
};
export const buildProgramsFilter = (
  selectedPrograms = [],
  allProgramIdsRef,
  initialProgramsRef
) => {
  const allIds = allProgramIdsRef.current;
  const initialIds = initialProgramsRef.current;

  const selectedIds = selectedPrograms.map(p => p.id ?? p);

  // 🟢 NOT CHANGED → use "all"
  if (
    selectedIds.length === initialIds.length &&
    selectedIds.every(id => initialIds.includes(id))
  ) {
    return { programs: ["all"] };
  }

  const unselectedIds = allIds.filter(
    id => !selectedIds.includes(id)
  );

  // 🔁 Optimization
  if (selectedIds.length > unselectedIds.length) {
    return { program_not_in: unselectedIds };
  }

  return { programs: selectedIds };
};

export const TableFilter = ({ config, filter, setFilter, setUseFilter, download, exportData, exportLink, exportHeaders, loading, isExporting, inventoryTypes, roles, organization, reportName }) => {

    const defaultFilters = {
        keyword: '',
        role_id: '',
        from: defaultFrom,
        to: defaultTo,
        inventoryType: 0,
        org: false,
        orgOptions: []
    }
    const initialProgramsRef = useRef(
        filter.programs && filter.programs.length ? [...filter.programs] : []
    );
    const finalFilter = { ...defaultFilters, ...filter }
    let finalFilterPrograms = clone(filter.programs)

    const defaultConfig = {
        label: 'term',
        keyword: true,
        roles: true,
        dateRange: false,
        inventoryType: !!inventoryTypes,
        organizations: false
    }

    const options = { ...defaultConfig, ...config }

    // console.log(options)

    const [keyword, setKeyword] = React.useState(finalFilter.keyword)
    const [org, setOrg] = React.useState(finalFilter.org)
    const [orgOptions, setOrgOptions] = React.useState(finalFilter.orgOptions)
    const [roleId, setRoleId] = useState(finalFilter.role_id)
    const [sku, setSku] = React.useState(finalFilter.sku)
    const [from, setFrom] = React.useState(finalFilter.from)
    const [to, setTo] = React.useState(finalFilter.to)
    const [inventoryType, setInventoryTypes] = React.useState(finalFilter.inventoryType)
    const [awardLevels, setAwardLevels] = React.useState(finalFilter.awardLevels);
    const [selectedPrograms, setSelectedPrograms] = useState(filter.programs ? filter.programs : []);
    const [selectedMerchants, setSelectedMerchants] = useState(filter.merchants ? filter.merchants : []);

    const [savedFilters, setSavedFilters] = useState([]);
    const [loadingSavedFilters, setLoadingSavedFilters] = useState(false);
    const [selectedSavedFilterId, setSelectedSavedFilterId] = useState('');

    useEffect(() => {
        if (!options.savedFilters || !organization?.id) return;
        let cancelled = false;
        setLoadingSavedFilters(true);
        getReportFilterPresets(organization.id, options.savedFilters)
            .then((data) => {
                if (!cancelled) setSavedFilters(Array.isArray(data) ? data : []);
            })
            .catch(() => {})
            .finally(() => {
                if (!cancelled) setLoadingSavedFilters(false);
            });
        return () => { cancelled = true };
    }, [options.savedFilters, organization?.id]);

    const savedFilterOptions = savedFilters.map((preset) => ({ value: preset.id, label: preset.name }));

    // Captures every filter dimension enabled for this report (not just programs),
    // so a saved filter reproduces the full filter state (dates, merchants, award levels, etc.)
    const buildFilterSnapshot = () => {
        const snapshot = {};
        if (options.keyword) snapshot.keyword = keyword;
        if (options.role) snapshot.role_id = roleId;
        if (options.sku) snapshot.sku = sku;
        if (options.dateRange) {
            snapshot.from = dateStrToYmd(from);
            snapshot.to = dateStrToYmd(to);
        } else if (options.date) {
            snapshot.from = dateStrToYmd(from);
        }
        if (options.inventoryType) snapshot.inventoryType = inventoryType;
        if (options.programs) snapshot.programs = clone(selectedPrograms);
        if (options.merchants) snapshot.merchants = clone(selectedMerchants);
        if (options.awardLevels) snapshot.awardLevels = clone(awardLevels);
        return snapshot;
    };

    const applyFilterSnapshot = (snapshot) => {
        if (!snapshot) return;
        if (options.keyword && snapshot.keyword !== undefined) setKeyword(snapshot.keyword);
        if (options.role && snapshot.role_id !== undefined) setRoleId(snapshot.role_id);
        if (options.sku && snapshot.sku !== undefined) setSku(snapshot.sku);
        if (options.dateRange) {
            if (snapshot.from) setFrom(new Date(snapshot.from));
            if (snapshot.to) setTo(new Date(snapshot.to));
        } else if (options.date && snapshot.from) {
            setFrom(new Date(snapshot.from));
        }
        if (options.inventoryType && snapshot.inventoryType !== undefined) setInventoryTypes(snapshot.inventoryType);
        if (options.programs && snapshot.programs) setSelectedPrograms(clone(snapshot.programs));
        if (options.merchants && snapshot.merchants) setSelectedMerchants(clone(snapshot.merchants));
        if (options.awardLevels && snapshot.awardLevels) setAwardLevels(clone(snapshot.awardLevels));
    };

    const onSelectSavedFilter = (option) => {
        setSelectedSavedFilterId(option ? option.value : '');
        if (!option) return;
        const preset = savedFilters.find((f) => f.id === option.value);
        if (!preset?.filters) return;
        applyFilterSnapshot(preset.filters);
        onClickFilter(false, 0, preset.filters);
    };

    const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);
    const [newFilterName, setNewFilterName] = useState('');
    const [savingFilter, setSavingFilter] = useState(false);

    const openSaveFilterModal = () => {
        setNewFilterName('');
        setShowSaveFilterModal(true);
    };

    const closeSaveFilterModal = () => {
        if (savingFilter) return;
        setShowSaveFilterModal(false);
    };

    const onSaveNewFilter = async () => {
        const name = newFilterName.trim();
        if (!name) return;
        setSavingFilter(true);
        try {
            const created = await createReportFilterPreset(organization.id, {
                report_key: options.savedFilters,
                name,
                filters: buildFilterSnapshot()
            });
            setSavedFilters((prev) => [...prev, created]);
            setSelectedSavedFilterId(created.id);
            setShowSaveFilterModal(false);
        } catch (e) {
            window.alert(`Could not save filter. ${e?.message || ''}`);
        } finally {
            setSavingFilter(false);
        }
    };

    const onUpdateSavedFilter = async () => {
        const preset = savedFilters.find((f) => f.id === selectedSavedFilterId);
        if (!preset) return;
        try {
            const snapshot = buildFilterSnapshot();
            const updated = await updateReportFilterPreset(organization.id, preset.id, {
                report_key: preset.report_key,
                name: preset.name,
                filters: snapshot
            });
            setSavedFilters((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
            onClickFilter(false, 0, snapshot);
        } catch (e) {
            window.alert(`Could not update filter. ${e?.message || ''}`);
        }
    };

    const onDeleteSavedFilter = async () => {
        if (!selectedSavedFilterId) return;
        if (!window.confirm('Delete this saved filter?')) return;
        try {
            await deleteReportFilterPreset(organization.id, selectedSavedFilterId);
            setSavedFilters((prev) => prev.filter((f) => f.id !== selectedSavedFilterId));
            setSelectedSavedFilterId('');
        } catch (e) {
            window.alert(`Could not delete filter. ${e?.message || ''}`);
        }
    };

    let roleOptions = roles?.map((role) => ({ label: role.name, value: role.id }));
    roleOptions?.unshift({ label: 'All Roles', value: '' });

    const onKeywordChange = (e) => {
        setKeyword(e.target.value)
    }
    const onRoleChange = (e) => {
        setRoleId(e.value);
    };
    const onSkuChange = (e) => {
        setSku(e.target.value)
    }
    const onStartChange = (value) => {
        setFrom(value)
    }
    const onEndChange = (value) => {
        setTo(value)
    }
    const onInventoryTypesChange = (e) => {
        setInventoryTypes(e.value)
    }

    const onOrgChange = (selectedOption) => {
        setOrg(selectedOption)
        let newOrg = { name: selectedOption.label, id: parseInt(selectedOption.value) }
        store.dispatch(setOrganization(newOrg))
        setAuthOrganization(newOrg);
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            onClickFilter();
        }
    };

    const onClickFilter = (reset = false, exportToCsv = 0, overrides = {}) => {

        let dataSet = {};

        if (options.keyword) {
            dataSet.keyword = reset ? '' : (overrides.keyword !== undefined ? overrides.keyword : keyword);
        }

        if (options.role) {
            dataSet.role_id = reset ? '' : (overrides.role_id !== undefined ? overrides.role_id : roleId);
        }

        if (options.sku) {
            dataSet.sku = reset ? '' : (overrides.sku !== undefined ? overrides.sku : sku);
        }

        if (options.dateRange) {
            dataSet.from = dateStrToYmd(reset ? defaultFrom : (overrides.from !== undefined ? overrides.from : from));
            dataSet.to = dateStrToYmd(reset ? defaultTo : (overrides.to !== undefined ? overrides.to : to));
        }

        else if (options.date) {
            dataSet.from = dateStrToYmd(reset ? defaultFrom : (overrides.from !== undefined ? overrides.from : from));
        }

        if (options.type) {
            dataSet.type = options.type;
        }
        if (options.inventoryType) {
            dataSet.inventoryType = overrides.inventoryType !== undefined ? overrides.inventoryType : inventoryType;
        }
        if (options.programs) {
            if (reset) {
                dataSet.programs = [];
            } else {
                const effectivePrograms = overrides.programs !== undefined ? overrides.programs : selectedPrograms;
                const initialPrograms = initialProgramsRef.current;
                const isUnchanged =
                    isEqual(
                        [...initialPrograms].sort(),
                        [...effectivePrograms].sort()
                    );

                dataSet.programs = isUnchanged ? ['all'] : clone(effectivePrograms);
            }
        }
        if (options.merchants) {
            dataSet.merchants = reset ? [] : clone(overrides.merchants !== undefined ? overrides.merchants : selectedMerchants);
        }
        if (options.awardLevels) {
            dataSet.awardLevels = reset ? [] : clone(overrides.awardLevels !== undefined ? overrides.awardLevels : awardLevels);
        }

        if (options.organization) {
            dataSet.org = org;
        }


        onClickFilterCallback(dataSet);

        if (reset) {
            setKeyword('');
            setRoleId('');
            setFrom(defaultFrom);
            setTo(defaultTo);
            setSelectedPrograms([]);
            setSelectedMerchants([]);
            setAwardLevels([]);
            setOrg('');
        }
    };
    const awardLevelAll = () => {
        let all = options.awardLevels.map(award => award.value);
        if (isEqual(awardLevels, all)) {
            setAwardLevels([]);
        } else {
            setAwardLevels(all);
        }
    };

    const onClickFilterCallback = (values) => {

        let change = false;

        if (options.keyword) {
            if (finalFilter.keyword !== values.keyword) {
                change = true
            }
        }

        if (options.sku) {
            if (finalFilter.sku !== values.sku) {
                change = true
            }
        }

        if (options.roles) {
            if (finalFilter.role_id !== values.role_id) {
                change = true
            }
        }

        if (options.programs) {
            if (!isEqual(finalFilterPrograms, values.programs)) {
                finalFilterPrograms = clone(values.programs);
                change = true
            }
        }

        if (options.merchants) {
            if (!isEqual(finalFilter.merchants, values.merchants)) {
                change = true
            }
        }

        if (options.awardLevels) {
            if (!isEqual(finalFilter.awardLevels, values.awardLevels)) {
                change = true
            }
        }

        if (options.dateRange) {
            if (finalFilter.from !== values.from || finalFilter.to !== values.to) {
                change = true
            }
        }

        if (options.date) {
            if (finalFilter.from !== values.from) {
                change = true
            }
        }

        if (options.inventoryType) {
            if (finalFilter.inventoryType !== values.inventoryType) {
                change = true
            }
        }


        if (options.organization) {
            if (finalFilter.org.value !== values.org.value) {
                change = true
            }
        }

        if (!change) {
            alert('No change in filters')
            setUseFilter(false)
            return
        }

        let filters = {}
        if (options.keyword) filters.keyword = values.keyword
        if (options.roles) filters.role_id = values.role_id
        if (options.sku) filters.sku = values.sku
        if (options.programs) {
            filters.programs = values.programs
        }
        if (options.merchants) {
            filters.merchants = values.merchants
        }
        if (options.awardLevels) {
            filters.awardLevels = values.awardLevels
        }
        if (options.dateRange) {
            filters.from = values.from
            filters.to = values.to
        }
        if (options.date) {
            filters.from = values.from
        }
        if (options.type) {
            filters.type = values.type
        }
        if (options.inventoryType) {
            filters.inventoryType = values.inventoryType
        }
        if (options.organization) {
            filters.organization = values.org.value;
        }

        setFilter(filters)
        setUseFilter(true)
    }

    return (
        <Row className="table-filter-form form" >
            <Col md={8} lg={8} sm={8} className="table-filter-form-fields" >
                <div className="ml-3">
                    <Row className="table-filter-form form"  >
                        {options.awardLevels &&
                            <div className="table-filter-form-col table-filter-form-col1 float-filter" style={{ paddingTop: 4 }}>
                                <div className="">
                                    <span className="form__form-group-label" onClick={awardLevelAll}
                                        style={{ cursor: 'pointer' }}
                                    >View for Award Level</span>
                                    <div className="form__form-group-field">
                                        <div className="form__form-group-row">
                                            <MultipleSelectField
                                                name="view_for_award_level"
                                                options={options.awardLevels}
                                                type="native"
                                                setValue={setAwardLevels}
                                                fieldValue={awardLevels}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        }
                        {options.programs &&
                            <div className="table-filter-form-col table-filter-form-col1 float-filter" style={{ paddingTop: 4 }}>
                                <div className="form__form-group">
                                    <div className="form__form-group-field">
                                        <div className="form__form-group-row">
                                            <ProgramsHierarchy
                                                defaultPrograms={options.programs}
                                                selectedPrograms={selectedPrograms}
                                                setSelectedPrograms={setSelectedPrograms}
                                                params={options.programParams}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        }
                        {options.organization &&
                            <Col lg="6" >
                                <Row className="table-filter-form form" >
                                    <Col lg="4" style={{ alignItems: 'center', display: 'flex' }} >Organization:</Col>
                                    <Col lg="8" >
                                        <Select
                                            value={org}
                                            onChange={onOrgChange}
                                            options={orgOptions}
                                            clearable={false}
                                            className="react-select"
                                            classNamePrefix="react-select"
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        }
                        {options.merchants &&
                            <div className="table-filter-form-col table-filter-form-col1 float-filter" style={{ paddingTop: 4 }}>
                                <div className="form__form-group">
                                    <div className="form__form-group-field">
                                        <div className="form__form-group-row">
                                            <MerchantsHierarchy
                                                defaultMerchants={options.merchants}
                                                selectedMerchants={selectedMerchants}
                                                setSelectedMerchants={setSelectedMerchants}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        }
                        {options.keyword &&
                            <Col lg={6}>
                                <div className="table-filter-form-col table-filter-form-col1 ">
                                    <div className="form__form-group">
                                        <div className="form__form-group-field">
                                            <div className="form__form-group-row">
                                                <input
                                                    value={keyword}
                                                    onChange={onKeywordChange}
                                                    onKeyDown={handleKeyDown}
                                                    type="text"
                                                    placeholder={`Search ${options.label}`}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        }
                        {options.role && (
                            roles && roles.length > 0 ? (
                                <Col lg={6}>
                                    <div className="table-filter-form-col table-filter-form-col1">
                                        <div className="form__form-group">
                                            <div className="form__form-group-field">
                                                <Select
                                                    options={roleOptions}
                                                    defaultValue={roleOptions && roleOptions[0]}
                                                    clearable={false}
                                                    className="react-select"
                                                    classNamePrefix="react-select"
                                                    onChange={onRoleChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            ) : (
                                <div>Loading roles...</div>
                            )
                        )}
                        {options.sku &&
                            <>
                                <div className="table-filter-form-col table-filter-form-col1 mt-2">
                                    <div className="form__form-group">
                                        <div className="form__form-group-field">
                                            <div className="form__form-group-row">
                                                <input
                                                    value={sku}
                                                    onChange={onSkuChange}
                                                    onKeyDown={handleKeyDown}
                                                    type="text"
                                                    placeholder={`Search sku`}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        }
                        {options.date &&
                            <>
                                <div className="table-filter-form-col table-filter-form-col2 float-filter">
                                    <div className="form__form-group">
                                        <span className="form__form-group-label">Through&nbsp;Date</span>
                                        <div className="form__form-group-field">
                                            <div className="form__form-group-row">
                                                <DatePicker
                                                    dateFormat="MM/dd/yyyy"
                                                    selected={from}
                                                    onChange={onStartChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        }
                        {options.inventoryType &&
                            <>
                                <div className="table-filter-form-col table-filter-form-col2 float-filter">
                                    <div className="form__form-group">
                                        <span className="form__form-group-label">Show</span>
                                        <div className="form__form-group-field">
                                            <div className="form__form-group-row">
                                                <Select
                                                    options={inventoryTypes}
                                                    clearable={false}
                                                    className="react-select"
                                                    classNamePrefix="react-select"
                                                    onChange={onInventoryTypesChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        }
                        {options.dateRange &&
                            <>
                                <div className="table-filter-form-col table-filter-form-col2 float-filter">
                                    <div className="form__form-group">
                                        <span className="form__form-group-label">From</span>
                                        <div className="form__form-group-field">
                                            <div className="form__form-group-row">
                                                <DatePicker
                                                    dateFormat="MM/dd/yyyy"
                                                    selected={from}
                                                    onChange={onStartChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="table-filter-form-col table-filter-form-col2 float-filter">
                                    <div className="form__form-group">
                                        <span className="form__form-group-label">To</span>
                                        <div className="form__form-group-field">
                                            <div className="form__form-group-row">
                                                <DatePicker
                                                    dateFormat="MM/dd/yyyy"
                                                    selected={to}
                                                    onChange={onEndChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        }
                    </Row>
                    {options.savedFilters &&
                        <Row className="table-filter-form form">
                            <div className="table-filter-form-col" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, paddingTop: 4 }}>
                                <span className="form__form-group-label" style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>Saved Filters</span>
                                <div style={{ minWidth: 200 }}>
                                    <Select
                                        value={savedFilterOptions.find((o) => o.value === selectedSavedFilterId) || null}
                                        onChange={onSelectSavedFilter}
                                        options={savedFilterOptions}
                                        isClearable
                                        isLoading={loadingSavedFilters}
                                        placeholder="Select saved filter..."
                                        className="react-select"
                                        classNamePrefix="react-select"
                                    />
                                </div>
                                <Button
                                    type="button"
                                    className="btn btn-sm btn-secondary m-0"
                                    onClick={openSaveFilterModal}
                                    title="Save current program selection as a new filter"
                                >Save</Button>
                                {selectedSavedFilterId &&
                                    <>
                                        <Button
                                            type="button"
                                            className="btn btn-sm btn-secondary m-0"
                                            onClick={onUpdateSavedFilter}
                                            title="Update saved filter with current program selection"
                                        >Update</Button>
                                        <Button
                                            type="button"
                                            className="btn btn-sm btn-danger m-0"
                                            onClick={onDeleteSavedFilter}
                                            title="Delete saved filter"
                                        >Delete</Button>
                                    </>
                                }
                                <Modal isOpen={showSaveFilterModal} toggle={closeSaveFilterModal} centered size="sm">
                                    <ModalHeader toggle={closeSaveFilterModal}>Save Filter</ModalHeader>
                                    <ModalBody>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={newFilterName}
                                            onChange={(e) => setNewFilterName(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    onSaveNewFilter();
                                                }
                                            }}
                                            placeholder="Filter name"
                                            autoFocus
                                        />
                                    </ModalBody>
                                    <ModalFooter>
                                        <Button
                                            type="button"
                                            className="btn btn-sm btn-secondary"
                                            onClick={closeSaveFilterModal}
                                            disabled={savingFilter}
                                        >Cancel</Button>
                                        <Button
                                            type="button"
                                            className="btn btn-sm btn-primary"
                                            onClick={onSaveNewFilter}
                                            disabled={!newFilterName.trim() || savingFilter}
                                        >{savingFilter ? 'Saving...' : 'Save'}</Button>
                                    </ModalFooter>
                                </Modal>
                            </div>
                        </Row>
                    }
                </div>
            </Col>
            <Col className="align-items-center  pl-1">
                <Button
                    onClick={() => onClickFilter()}
                    className="btn btn-sm btn-primary"
                    color="#ffffff"
                    disabled={loading}
                >Filter</Button>
                <Button
                    onClick={() => onClickFilter(true)}
                    className="btn btn-sm btn-secondary"
                    color="#ffffff"
                >Reset</Button>
                {options.exportToCsv &&
                    <div>
                        <ExportButton
                            isExporting={isExporting}
                            onExport={() => download(filter)}
                        />
                        <CSVLink
                            data={exportData}
                            headers={exportHeaders}
                            filename={reportName || "report.csv"}
                            className="hidden"
                            ref={exportLink}
                            target="_blank"
                            disabled={loading}
                        />
                    </div>
                }
            </Col>
        </Row>

    )
}

export default withRouter(connect((state) => ({
    auth: state.auth,
    organization: state.organization
}))(TableFilter));

// The CSV Table Error Building

export const makeCsvErrors = (csv_errors) => {
    const csv_errors_json = JSON.parse(csv_errors);
    if (csv_errors_json.errors && csv_errors_json.rows && csv_errors_json.errors.length === csv_errors_json.rows.length && csv_errors_json.rows.length > 0) {
        const csvErrors = csv_errors_json.errors;
        const csvHeaderRow = Object.keys(csv_errors_json.rows[0])
        // console.log(csvErrors)
        const CSV_COLUMNS = makeCsvColumns(csvHeaderRow)
        let csvRows = csv_errors_json.rows
        csvRows.map((row, i) => {
            // console.log(csvErrors[i])
            for (var key in row) {
                // console.log( key + ": " + row[key]);
                // console.log(csvErrors[i][key])
                if (csvErrors[i] && typeof csvErrors[i][key] !== 'undefined') {
                    csvRows[i][key] += `<span className="csv-row-error">${csvErrors[i][key]}</span>`;
                }
            }
        })
        return {
            columns: CSV_COLUMNS,
            rows: csvRows
        }
    }
}

const makeCsvColumns = (row) => {
    const CSV_COLUMNS = []
    row.map(item => {
        CSV_COLUMNS.push(
            {
                Header: item,
                accessor: item,
                Cell: row => (<div dangerouslySetInnerHTML={{ __html: row.value }} />)
            }
        )
    })
    // console.log(CSV_COLUMNS)
    return CSV_COLUMNS
}

export const Sorting = ({ column }) => (
    <span className="react-table__column-header sortable">
        {column.isSortedDesc === undefined ? (
            <SortIcon />
        ) : (
            <span>
                {column.isSortedDesc
                    ? <SortAscendingIcon />
                    : <SortDescendingIcon />}
            </span>
        )}
    </span>
);
